import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { Card, Checkbox, Collapse, Divider, Flex, Input, Tag, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useState } from "react";
import { VisitType } from "../../../domain/notes.domain";
import { PRACTICE_AREA } from "../../../domain/user.domain";
import { useAppSelector } from "../../../hooks/redux-hooks";
import useSpecialityReference from "../../../hooks/useSpecialityReference";
import SelectableBadge from "../../Badge/SelectableBadge.component";
import styles from "./AITemplateModal.module.scss";
import PromptMentionTextArea, { type PromptMentionTextAreaRef } from "./PromptMentionTextArea";
import {
  INSTRUCTIONS_FOR_PROMPT_PLACEHOLDER_BH,
  INTRUCTIONS_FOR_PROMPT_PLACEHOLDER,
  NOTE_TEMPLATE_CLIENT,
  NOTE_TEMPLATE_PATIENT,
} from "./constants";
import { type CustomNote } from "./types";

interface TemplateState {
  name: string;
  document: string;
  prompt: string;
}

const SECTION = {
  NAME: "name",
  DOCUMENT: "document",
  PROMPT: "prompt",
  CODING: "coding-instructions",
  VISIT_TYPE: "visit-type",
} as const;

interface SectionProps {
  sectionKey: string;
  title: ReactNode;
  subtitle?: ReactNode;
  isOpen: boolean;
  onToggle: (key: string) => void;
  children: ReactNode;
}

// Every top-level part of the form is a collapsible bar so the headers read as
// clickable. Declared at module scope: an inline component would remount on each
// render and blow away focus in the inputs it wraps.
const Section = ({ sectionKey, title, subtitle, isOpen, onToggle, children }: SectionProps) => (
  <Collapse
    className={styles["ai-template-modal__section"]}
    activeKey={isOpen ? [sectionKey] : []}
    onChange={() => onToggle(sectionKey)}
    expandIconPosition="end"
    expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} />}
    items={[
      {
        key: sectionKey,
        label: (
          <div>
            <span className={styles["ai-template-modal__section-title"]}>{title}</span>
            {subtitle && <span className={styles["ai-template-modal__section-subtitle"]}>{subtitle}</span>}
          </div>
        ),
        children,
      },
    ]}
  />
);

export interface CreateFormRef {
  validateForm: () => boolean;
}

interface CreateProps {
  setCurrentTemplate: (template: CustomNote | null) => void;
  isEditing: boolean;
  currentTemplate: CustomNote | null;
  handleGoBack: () => void;
  focusInstructions?: boolean;
}

const Create = forwardRef<CreateFormRef, CreateProps>(
  ({ setCurrentTemplate, isEditing, currentTemplate, handleGoBack, focusInstructions }, ref) => {
    const componentName = "ai-template-modal";
    const promptTextAreaRef = useRef<PromptMentionTextAreaRef>(null);
    const [template, setTemplate] = React.useState<TemplateState>({
      name: isEditing ? currentTemplate?.name ?? "" : "",
      document: isEditing ? currentTemplate?.document ?? "" : "",
      prompt: isEditing ? currentTemplate?.prompt ?? "" : "",
    });

    const [showTutorial, setShowTutorial] = React.useState(false);
    const [is_addon, setIsAddon] = React.useState(currentTemplate?.settings?.is_addon || false);
    const [is_cpt_codes, setIsCptCodes] = React.useState(currentTemplate?.settings?.is_cpt_codes || false);
    const [is_group_accessible, setIsGroupAccessible] = React.useState(currentTemplate?.is_group_accessible || false);
    const [visit_type, setVisitType] = React.useState<number | undefined>(currentTemplate?.settings?.visit_type);
    const [coding_instructions, setCodingInstructions] = React.useState<string>(
      currentTemplate?.coding_instructions ?? "",
    );
    const [isVideoPopoverVisible, setIsVideoPopoverVisible] = React.useState(false);
    const [showValidationErrors, setShowValidationErrors] = React.useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Sections that were always visible before stay open; procedural coding stays
    // closed unless the template already carries instructions.
    const [openSections, setOpenSections] = useState<string[]>(() =>
      [
        SECTION.NAME,
        SECTION.DOCUMENT,
        SECTION.PROMPT,
        SECTION.VISIT_TYPE,
        ...(currentTemplate?.coding_instructions ? [SECTION.CODING] : []),
      ].map(String),
    );

    const toggleSection = useCallback((key: string) => {
      setOpenSections((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
    }, []);

    const openSection = useCallback((...keys: string[]) => {
      setOpenSections((current) => {
        const missing = keys.filter((key) => !current.includes(key));
        return missing.length ? [...current, ...missing] : current;
      });
    }, []);

    const { userInfo } = useAppSelector((state) => state.auth);
    const { shouldReferAsPatient } = useSpecialityReference();

    // Validation errors
    const validationErrors = useMemo(
      () => ({
        name: template.name.length === 0,
        document: template.document.length === 0,
        visit_type: visit_type === undefined,
      }),
      [template.name, template.document, visit_type],
    );

    const hasValidationErrors = validationErrors.name || validationErrors.document || validationErrors.visit_type;

    // Expose validateForm method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validateForm: () => {
          if (hasValidationErrors) {
            setShowValidationErrors(true);
            // Expand whatever is missing, otherwise the error sits inside a
            // collapsed section where nobody can see it.
            openSection(
              ...[
                ...(validationErrors.name ? [SECTION.NAME] : []),
                ...(validationErrors.document ? [SECTION.DOCUMENT] : []),
                ...(validationErrors.visit_type ? [SECTION.VISIT_TYPE] : []),
              ],
            );
            return false;
          }
          return true;
        },
      }),
      [hasValidationErrors, validationErrors, openSection],
    );

    // Initialize template from currentTemplate
    useEffect(() => {
      if (currentTemplate) {
        setTemplate({
          name: currentTemplate.name || "",
          document: currentTemplate.document || "",
          prompt: currentTemplate.prompt || "",
        });
        setIsAddon(currentTemplate.settings?.is_addon || false);
        setIsCptCodes(currentTemplate.settings?.is_cpt_codes || false);
        setIsGroupAccessible(currentTemplate.is_group_accessible || false);
        setVisitType(currentTemplate.settings?.visit_type);
        setCodingInstructions(currentTemplate.coding_instructions ?? "");
        if (currentTemplate.coding_instructions) {
          openSection(SECTION.CODING);
        }
      }
    }, [currentTemplate, openSection]); // Only run when template ID changes

    // Handle template changes
    const handleTemplateChange = useCallback(
      (newTemplate: TemplateState) => {
        setTemplate(newTemplate);
        setCurrentTemplate({
          ...(currentTemplate || {}),
          name: newTemplate.name,
          document: newTemplate.document,
          prompt: newTemplate.prompt,
          coding_instructions,
          settings: {
            is_addon,
            is_cpt_codes,
            visit_type,
          },
          is_group_accessible,
        } as CustomNote);
      },
      [
        currentTemplate,
        is_addon,
        is_cpt_codes,
        visit_type,
        coding_instructions,
        is_group_accessible,
        setCurrentTemplate,
      ],
    );

    useEffect(() => {
      const tutorialState = localStorage.getItem("tutorialState");
      if (tutorialState) {
        setShowTutorial(tutorialState === "true");
      } else {
        setShowTutorial(true);
      }
    }, []);

    // Handle click outside to close popover
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
          setIsVideoPopoverVisible(false);
        }
      };

      if (isVideoPopoverVisible) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isVideoPopoverVisible]);

    // Scroll to and focus the prompt textarea when focusInstructions is true
    useEffect(() => {
      if (focusInstructions) {
        openSection(SECTION.PROMPT);
        setTimeout(() => {
          promptTextAreaRef.current?.scrollIntoView();
          promptTextAreaRef.current?.focus();
        }, 100);
      }
    }, [focusInstructions, openSection]);

    return (
      <div style={{ height: "calc(100vh - 60px)", display: "flex", flexDirection: "column", position: "relative" }}>
        <div className={styles[`${componentName}__header`]}>
          <div className={styles[`${componentName}__header-container-create`]}>
            <button onClick={handleGoBack} className={styles[`${componentName}__header-close-button`]}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                width={20}
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              </svg>
            </button>

            <h3>{isEditing ? "Edit" : "Create"} My Template</h3>
          </div>
        </div>

        <Divider style={{ marginBlock: "0" }} />

        {showTutorial && (
          <a href="https://getnudgeai.com/custom-templates" target="_blank" rel="noopener noreferrer">
            <div className={styles[`${componentName}__tutorial-container`]}>
              <div>
                <div>
                  <img src="/assets/tutorial.png" alt="tutorial" width={57} height={40} />
                  <div>
                    <h4>How to create a custom template using AI</h4>
                    <p>Learn through our interactive tutorial and start building in just 2 steps</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    localStorage.setItem("tutorialState", "false");
                    setShowTutorial(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    width={20}
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </a>
        )}

        <div
          className={styles[`${componentName}__create-container`]}
          style={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto", paddingBottom: "80px" }}
        >
          <Section
            sectionKey={SECTION.NAME}
            title="Name your template"
            isOpen={openSections.includes(SECTION.NAME)}
            onToggle={toggleSection}
          >
            <Input
              placeholder="Type template name"
              value={template.name}
              onChange={(e) =>
                handleTemplateChange({
                  name: e.target.value,
                  document: template.document,
                  prompt: template.prompt,
                })
              }
              maxLength={100}
              status={showValidationErrors && validationErrors.name ? "error" : undefined}
            />
            {showValidationErrors && validationErrors.name && (
              <Typography.Text type="danger" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
                This field is required
              </Typography.Text>
            )}
          </Section>
          <Section
            sectionKey={SECTION.DOCUMENT}
            title={
              <>
                Paste a <span style={{ textDecoration: "underline" }}>fully completed</span> note with every section
                filled in as your template
              </>
            }
            subtitle="Eg: progress notes, intakes, treatment plans, assessments, discharges and more."
            isOpen={openSections.includes(SECTION.DOCUMENT)}
            onToggle={toggleSection}
          >
            <Flex
              gap={10}
              align="center"
              style={{ marginBottom: "10px", cursor: "pointer" }}
              onClick={() => {
                window.open("https://getnudgeai.com/custom-templates", "_blank");
              }}
            >
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M14.9807 0.801003C14.9348 0.574945 14.8122 0.371708 14.6336 0.225727C14.455 0.0797464 14.2314 0 14.0007 0C13.7701 0 13.5465 0.0797464 13.3679 0.225727C13.1893 0.371708 13.0666 0.574945 13.0207 0.801003L12.7807 1.993C12.7421 2.18663 12.6471 2.3645 12.5076 2.5042C12.3681 2.64389 12.1903 2.73916 11.9967 2.778L10.8047 3.016C10.5776 3.06092 10.3731 3.18328 10.2261 3.36218C10.0791 3.54109 9.99873 3.76546 9.99873 3.997C9.99873 4.22855 10.0791 4.45292 10.2261 4.63182C10.3731 4.81073 10.5776 4.93308 10.8047 4.978L11.9967 5.216C12.1905 5.25466 12.3685 5.34986 12.5082 5.48957C12.6479 5.62928 12.7431 5.80724 12.7817 6.001L13.0197 7.193C13.0646 7.42015 13.187 7.62467 13.3659 7.77166C13.5448 7.91864 13.7692 7.99899 14.0007 7.99899C14.2323 7.99899 14.4566 7.91864 14.6355 7.77166C14.8144 7.62467 14.9368 7.42015 14.9817 7.193L15.2197 6.001C15.2584 5.80724 15.3536 5.62928 15.4933 5.48957C15.633 5.34986 15.811 5.25466 16.0047 5.216L17.1967 4.978C17.4239 4.93308 17.6284 4.81073 17.7754 4.63182C17.9224 4.45292 18.0027 4.22855 18.0027 3.997C18.0027 3.76546 17.9224 3.54109 17.7754 3.36218C17.6284 3.18328 17.4239 3.06092 17.1967 3.016L16.0047 2.778C15.811 2.73934 15.633 2.64415 15.4933 2.50444C15.3536 2.36473 15.2584 2.18676 15.2197 1.993L14.9817 0.801003H14.9807ZM5.94972 4.681C5.8835 4.48166 5.75618 4.30824 5.58584 4.18533C5.4155 4.06242 5.21078 3.99628 5.00072 3.99628C4.79067 3.99628 4.58595 4.06242 4.41561 4.18533C4.24526 4.30824 4.11795 4.48166 4.05172 4.681L3.36872 6.732C3.31965 6.87944 3.23689 7.01341 3.12701 7.12329C3.01713 7.23317 2.88316 7.31593 2.73572 7.365L0.684724 8.048C0.485383 8.11423 0.311957 8.24154 0.189051 8.41189C0.0661451 8.58223 0 8.78695 0 8.997C0 9.20706 0.0661451 9.41178 0.189051 9.58212C0.311957 9.75246 0.485383 9.87978 0.684724 9.946L2.73572 10.63C2.88305 10.679 3.01696 10.7616 3.12683 10.8713C3.2367 10.981 3.31952 11.1147 3.36872 11.262L4.05172 13.313C4.11795 13.5123 4.24526 13.6858 4.41561 13.8087C4.58595 13.9316 4.79067 13.9977 5.00072 13.9977C5.21078 13.9977 5.4155 13.9316 5.58584 13.8087C5.75618 13.6858 5.8835 13.5123 5.94972 13.313L6.63272 11.262C6.6818 11.1146 6.76456 10.9806 6.87444 10.8707C6.98431 10.7608 7.11829 10.6781 7.26572 10.629L9.31672 9.946C9.51606 9.87978 9.68949 9.75246 9.8124 9.58212C9.9353 9.41178 10.0014 9.20706 10.0014 8.997C10.0014 8.78695 9.9353 8.58223 9.8124 8.41189C9.68949 8.24154 9.51606 8.11423 9.31672 8.048L7.26572 7.365C7.11829 7.31593 6.98431 7.23317 6.87444 7.12329C6.76456 7.01341 6.6818 6.87944 6.63272 6.732L5.94972 4.681ZM12.9497 12.681C12.8835 12.4817 12.7562 12.3082 12.5858 12.1853C12.4155 12.0624 12.2108 11.9963 12.0007 11.9963C11.7907 11.9963 11.5859 12.0624 11.4156 12.1853C11.2453 12.3082 11.1179 12.4817 11.0517 12.681L10.8677 13.232C10.8187 13.3793 10.7361 13.5132 10.6264 13.6231C10.5167 13.733 10.383 13.8158 10.2357 13.865L9.68472 14.048C9.48538 14.1142 9.31196 14.2415 9.18905 14.4119C9.06614 14.5822 9 14.7869 9 14.997C9 15.2071 9.06614 15.4118 9.18905 15.5821C9.31196 15.7525 9.48538 15.8798 9.68472 15.946L10.2357 16.129C10.3832 16.1781 10.5171 16.2608 10.627 16.3707C10.7369 16.4806 10.8196 16.6146 10.8687 16.762L11.0517 17.313C11.1179 17.5123 11.2453 17.6858 11.4156 17.8087C11.5859 17.9316 11.7907 17.9977 12.0007 17.9977C12.2108 17.9977 12.4155 17.9316 12.5858 17.8087C12.7562 17.6858 12.8835 17.5123 12.9497 17.313L13.1337 16.762C13.1827 16.6147 13.2653 16.4808 13.375 16.3709C13.4847 16.261 13.6185 16.1782 13.7657 16.129L14.3167 15.946C14.5161 15.8798 14.6895 15.7525 14.8124 15.5821C14.9353 15.4118 15.0014 15.2071 15.0014 14.997C15.0014 14.7869 14.9353 14.5822 14.8124 14.4119C14.6895 14.2415 14.5161 14.1142 14.3167 14.048L13.7657 13.864C13.6184 13.815 13.4845 13.7324 13.3746 13.6227C13.2648 13.513 13.1819 13.3793 13.1327 13.232L12.9497 12.681Z"
                  fill="#0070FF"
                />
              </svg>
              <span style={{ color: "#2677FD" }} className={styles[`${componentName}__video-link-text`]}>
                Click to learn from examples
              </span>
            </Flex>
            <TextArea
              placeholder={shouldReferAsPatient ? NOTE_TEMPLATE_PATIENT : NOTE_TEMPLATE_CLIENT}
              style={{
                height: "284px",
              }}
              value={template.document}
              onChange={(e) =>
                handleTemplateChange({
                  name: template.name,
                  document: e.target.value,
                  prompt: template.prompt,
                })
              }
              status={showValidationErrors && validationErrors.document ? "error" : undefined}
            />
            {showValidationErrors && validationErrors.document && (
              <Typography.Text type="danger" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
                This field is required
              </Typography.Text>
            )}
          </Section>
          <Section
            sectionKey={SECTION.PROMPT}
            title="Add Prompt Instructions to customize (add instructions like in ChatGPT)"
            subtitle="Add instructions like: rules, instructions, dotphrases, formatting preferences, tone, etc."
            isOpen={openSections.includes(SECTION.PROMPT)}
            onToggle={toggleSection}
          >
            <div style={{ position: "relative" }}>
              <Flex
                gap={10}
                align="center"
                style={{ marginBottom: "10px", cursor: "pointer" }}
                onClick={() => {
                  window.open("https://getnudgeai.com/directives-tutorials", "_blank");
                }}
              >
                <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14.9807 0.801003C14.9348 0.574945 14.8122 0.371708 14.6336 0.225727C14.455 0.0797464 14.2314 0 14.0007 0C13.7701 0 13.5465 0.0797464 13.3679 0.225727C13.1893 0.371708 13.0666 0.574945 13.0207 0.801003L12.7807 1.993C12.7421 2.18663 12.6471 2.3645 12.5076 2.5042C12.3681 2.64389 12.1903 2.73916 11.9967 2.778L10.8047 3.016C10.5776 3.06092 10.3731 3.18328 10.2261 3.36218C10.0791 3.54109 9.99873 3.76546 9.99873 3.997C9.99873 4.22855 10.0791 4.45292 10.2261 4.63182C10.3731 4.81073 10.5776 4.93308 10.8047 4.978L11.9967 5.216C12.1905 5.25466 12.3685 5.34986 12.5082 5.48957C12.6479 5.62928 12.7431 5.80724 12.7817 6.001L13.0197 7.193C13.0646 7.42015 13.187 7.62467 13.3659 7.77166C13.5448 7.91864 13.7692 7.99899 14.0007 7.99899C14.2323 7.99899 14.4566 7.91864 14.6355 7.77166C14.8144 7.62467 14.9368 7.42015 14.9817 7.193L15.2197 6.001C15.2584 5.80724 15.3536 5.62928 15.4933 5.48957C15.633 5.34986 15.811 5.25466 16.0047 5.216L17.1967 4.978C17.4239 4.93308 17.6284 4.81073 17.7754 4.63182C17.9224 4.45292 18.0027 4.22855 18.0027 3.997C18.0027 3.76546 17.9224 3.54109 17.7754 3.36218C17.6284 3.18328 17.4239 3.06092 17.1967 3.016L16.0047 2.778C15.811 2.73934 15.633 2.64415 15.4933 2.50444C15.3536 2.36473 15.2584 2.18676 15.2197 1.993L14.9817 0.801003H14.9807ZM5.94972 4.681C5.8835 4.48166 5.75618 4.30824 5.58584 4.18533C5.4155 4.06242 5.21078 3.99628 5.00072 3.99628C4.79067 3.99628 4.58595 4.06242 4.41561 4.18533C4.24526 4.30824 4.11795 4.48166 4.05172 4.681L3.36872 6.732C3.31965 6.87944 3.23689 7.01341 3.12701 7.12329C3.01713 7.23317 2.88316 7.31593 2.73572 7.365L0.684724 8.048C0.485383 8.11423 0.311957 8.24154 0.189051 8.41189C0.0661451 8.58223 0 8.78695 0 8.997C0 9.20706 0.0661451 9.41178 0.189051 9.58212C0.311957 9.75246 0.485383 9.87978 0.684724 9.946L2.73572 10.63C2.88305 10.679 3.01696 10.7616 3.12683 10.8713C3.2367 10.981 3.31952 11.1147 3.36872 11.262L4.05172 13.313C4.11795 13.5123 4.24526 13.6858 4.41561 13.8087C4.58595 13.9316 4.79067 13.9977 5.00072 13.9977C5.21078 13.9977 5.4155 13.9316 5.58584 13.8087C5.75618 13.6858 5.8835 13.5123 5.94972 13.313L6.63272 11.262C6.6818 11.1146 6.76456 10.9806 6.87444 10.8707C6.98431 10.7608 7.11829 10.6781 7.26572 10.629L9.31672 9.946C9.51606 9.87978 9.68949 9.75246 9.8124 9.58212C9.9353 9.41178 10.0014 9.20706 10.0014 8.997C10.0014 8.78695 9.9353 8.58223 9.8124 8.41189C9.68949 8.24154 9.51606 8.11423 9.31672 8.048L7.26572 7.365C7.11829 7.31593 6.98431 7.23317 6.87444 7.12329C6.76456 7.01341 6.6818 6.87944 6.63272 6.732L5.94972 4.681ZM12.9497 12.681C12.8835 12.4817 12.7562 12.3082 12.5858 12.1853C12.4155 12.0624 12.2108 11.9963 12.0007 11.9963C11.7907 11.9963 11.5859 12.0624 11.4156 12.1853C11.2453 12.3082 11.1179 12.4817 11.0517 12.681L10.8677 13.232C10.8187 13.3793 10.7361 13.5132 10.6264 13.6231C10.5167 13.733 10.383 13.8158 10.2357 13.865L9.68472 14.048C9.48538 14.1142 9.31196 14.2415 9.18905 14.4119C9.06614 14.5822 9 14.7869 9 14.997C9 15.2071 9.06614 15.4118 9.18905 15.5821C9.31196 15.7525 9.48538 15.8798 9.68472 15.946L10.2357 16.129C10.3832 16.1781 10.5171 16.2608 10.627 16.3707C10.7369 16.4806 10.8196 16.6146 10.8687 16.762L11.0517 17.313C11.1179 17.5123 11.2453 17.6858 11.4156 17.8087C11.5859 17.9316 11.7907 17.9977 12.0007 17.9977C12.2108 17.9977 12.4155 17.9316 12.5858 17.8087C12.7562 17.6858 12.8835 17.5123 12.9497 17.313L13.1337 16.762C13.1827 16.6147 13.2653 16.4808 13.375 16.3709C13.4847 16.261 13.6185 16.1782 13.7657 16.129L14.3167 15.946C14.5161 15.8798 14.6895 15.7525 14.8124 15.5821C14.9353 15.4118 15.0014 15.2071 15.0014 14.997C15.0014 14.7869 14.9353 14.5822 14.8124 14.4119C14.6895 14.2415 14.5161 14.1142 14.3167 14.048L13.7657 13.864C13.6184 13.815 13.4845 13.7324 13.3746 13.6227C13.2648 13.513 13.1819 13.3793 13.1327 13.232L12.9497 12.681Z"
                    fill="#0070FF"
                  />
                </svg>
                <span style={{ color: "#2677FD" }} className={styles[`${componentName}__video-link-text`]}>
                  Click to master the power of setting up your prompt for Nudge's AI
                </span>
              </Flex>

              {/* Video Popover */}
              {isVideoPopoverVisible && (
                <div ref={popoverRef} className={styles[`${componentName}__video-popover`]}>
                  <button
                    onClick={() => setIsVideoPopoverVisible(false)}
                    className={styles[`${componentName}__video-popover-close`]}
                  >
                    <CloseOutlined />
                  </button>
                  <div className={styles[`${componentName}__video-popover-content`]}>
                    <iframe
                      width="100%"
                      height="315"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      title="AI Prompting Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
            </div>
            <PromptMentionTextArea
              ref={promptTextAreaRef}
              height={284}
              placeholder={
                userInfo?.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH
                  ? INSTRUCTIONS_FOR_PROMPT_PLACEHOLDER_BH
                  : INTRUCTIONS_FOR_PROMPT_PLACEHOLDER
              }
              value={template.prompt}
              onChange={(prompt) =>
                handleTemplateChange({
                  name: template.name,
                  document: template.document,
                  prompt,
                })
              }
            />
            <Typography.Text
              style={{ color: "var(--grey-text)", fontSize: "12px", marginTop: "6px", display: "block" }}
            >
              Type <b>@</b> to add instructions for your digital MA (applied when transferring to your EHR) — put each
              @ma instruction on its own line.
            </Typography.Text>
          </Section>
          {userInfo?.practice_area === PRACTICE_AREA.MEDICINE && userInfo?.custom_coding_instructions_enabled && (
            <Section
              sectionKey={SECTION.CODING}
              title={
                <>
                  Add Procedural Coding (CPT) Instructions (optional)
                  <span className={styles[`${componentName}__section-beta`]}>Beta</span>
                </>
              }
              subtitle="Add rules for how visit and procedural codes are suggested for this template."
              isOpen={openSections.includes(SECTION.CODING)}
              onToggle={toggleSection}
            >
              <Flex vertical>
                <Typography.Text style={{ color: "var(--grey-text)", marginBottom: "10px" }}>
                  Add standing rules applied to every note created with this template, like level minimums, codes you
                  never bill, or how to choose a level. They guide choices among supported codes; they cannot add codes
                  the documentation does not support. For a decision about a single visit, say it during the encounter
                  ("bill this as a 99213 with the injection") — spoken directives take priority over these instructions.
                </Typography.Text>
                <TextArea
                  placeholder={`- Bill 99214 minimum for follow-up visits.
- For new-patient visits, bill 99204 at minimum.
- Do not bill 36415; we consider blood draws bundled.
- When MDM and documented time both support a level, prefer time-based selection.`}
                  style={{ height: "140px" }}
                  value={coding_instructions}
                  onChange={(e) => {
                    setCodingInstructions(e.target.value);
                    setCurrentTemplate({
                      ...(currentTemplate || {}),
                      name: template.name,
                      document: template.document,
                      prompt: template.prompt,
                      coding_instructions: e.target.value,
                      settings: {
                        is_addon,
                        is_cpt_codes,
                        visit_type,
                      },
                      is_group_accessible,
                    } as CustomNote);
                  }}
                />
              </Flex>
            </Section>
          )}
          <Section
            sectionKey={SECTION.VISIT_TYPE}
            title="What type of visit is this template for? (required)"
            isOpen={openSections.includes(SECTION.VISIT_TYPE)}
            onToggle={toggleSection}
          >
            <Flex wrap="wrap" gap={"5px 0px"}>
              {[
                { type: VisitType.TYPE_INTAKE, title: "Intake" },
                { type: VisitType.TYPE_FOLLOWUP, title: "Follow-up" },
                ...(userInfo?.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH
                  ? [{ type: VisitType.TYPE_TREATMENT_PLAN, title: "Treatment Plan" }]
                  : []),
                { type: VisitType.TYPE_OTHER, title: "Other" },
              ].map((visitTypeOption) => (
                <SelectableBadge
                  key={visitTypeOption.type}
                  count={visitTypeOption.title}
                  selected={visit_type === visitTypeOption.type}
                  onClick={() => {
                    setVisitType(visitTypeOption.type);
                    setCurrentTemplate({
                      ...(currentTemplate || {}),
                      name: template.name,
                      document: template.document,
                      prompt: template.prompt,
                      coding_instructions,
                      settings: {
                        is_addon,
                        is_cpt_codes,
                        visit_type: visitTypeOption.type,
                      },
                      is_group_accessible,
                    } as CustomNote);
                  }}
                />
              ))}
            </Flex>
            {showValidationErrors && validationErrors.visit_type && (
              <Typography.Text type="danger" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
                This field is required
              </Typography.Text>
            )}
          </Section>
          {!!userInfo?.group_name && (
            <div
              className={styles[`${componentName}__create-checkbox`]}
              style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
            >
              <div
                style={{
                  backgroundColor: "#E6F7FF",
                  borderRadius: "4px",
                  padding: "0px",
                }}
              >
                <Checkbox
                  onChange={(e) => {
                    setIsGroupAccessible(e.target.checked);
                    setCurrentTemplate({
                      ...(currentTemplate || {}),
                      name: template.name,
                      document: template.document,
                      prompt: template.prompt,
                      coding_instructions,
                      settings: {
                        is_addon,
                        is_cpt_codes,
                        visit_type,
                      },
                      is_group_accessible: e.target.checked,
                    } as CustomNote);
                  }}
                  checked={is_group_accessible}
                  style={{ color: "#0050B3" }}
                >
                  Share this template with my group members
                </Checkbox>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

Create.displayName = "Create";

export default Create;
